import { Button } from '@mui/material';
import { getSelectorAttribute, integrationsPageSelectors } from '@vy/app-selectors';
import { awaitAllSafely, isEmptyObject } from '@vy/utils';
import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntegrationContextProvider } from '../../../../hooks/use-integration-context-provider';
import { useConnectToStripe } from '../../hooks/connect-to-stripe';
import { useFetchStripeSettings } from '../hooks/useFetchStripeSettings';
import { useModifyConfiguration } from '../hooks/useModifyConfiguration';
import {
  type StripeConfigurationControl,
  type StripeConfigurationFormValues,
} from '../StripeConfigurationFormSchema';
import { getStripeIntegrationSettings } from './utils';

type StripeConfigurationFooterProps = {
  control: StripeConfigurationControl;
};

export const StripeConfigurationFooter: FC<StripeConfigurationFooterProps> = ({ control }) => {
  const { integrationId, configuration: existingConfiguration } = useFetchStripeSettings();
  const updateStripeConfiguration = useModifyConfiguration();
  const reconnectToStripe = useConnectToStripe(control);
  const { clearSelectedIntegration } = useIntegrationContextProvider();

  const {
    handleSubmit,
    formState: { dirtyFields },
    reset,
  } = useFormContext<StripeConfigurationFormValues>();

  const { apiToken: isApiTokenDirty, ...formDirtyValues } = dirtyFields;

  const getReconnectToStripePromise = async (apiToken: string) => {
    if (!isApiTokenDirty) {
      return undefined;
    }

    return reconnectToStripe(apiToken);
  };

  const getUpdateStipeConfigurationPromise = async (formValues: StripeConfigurationFormValues) => {
    if (isEmptyObject(formDirtyValues)) {
      return undefined;
    }

    const existingIntegrationSettings = getStripeIntegrationSettings(existingConfiguration);
    const updatedIntegrationSettings = getStripeIntegrationSettings(formValues);

    return updateStripeConfiguration(integrationId, updatedIntegrationSettings, existingIntegrationSettings);
  };

  const submitHandler = handleSubmit(async (formValues) => {
    const reconnectToStripePromise = getReconnectToStripePromise(formValues.apiToken);
    const updateStripeConfigurationPromise = getUpdateStipeConfigurationPromise(formValues);

    await awaitAllSafely(reconnectToStripePromise, updateStripeConfigurationPromise);

    reset(formValues);

    clearSelectedIntegration();
  });

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Button
        variant="contained"
        onClick={submitHandler}
        {...getSelectorAttribute(integrationsPageSelectors.drawer.stripe.configuration.saveButton)}
      >
        Save Configuration
      </Button>
    </div>
  );
};
